import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { PasswordStrengthBar } from "@/components/auth/PasswordStrengthBar";
import { formatAuthError, passwordStrength } from "@/lib/passwordStrength";

export default function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { updatePassword } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const strength = passwordStrength(password);
    if (!strength.acceptable) {
      toast({
        title: "Contraseña débil",
        description: strength.feedback[0] || "Usa al menos 8 caracteres con variedad.",
        variant: "destructive",
      });
      return;
    }
    if (password !== confirm) {
      toast({ title: "No coincide", description: "Las contraseñas no coinciden.", variant: "destructive" });
      return;
    }
    try {
      setIsSubmitting(true);
      await updatePassword(password);
      toast({ title: "Listo", description: "Tu contraseña fue actualizada." });
      navigate("/auth/login");
    } catch (error) {
      const raw = error instanceof Error ? error.message : "No se pudo actualizar la contraseña.";
      toast({ title: "Error", description: formatAuthError(raw), variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Nueva contraseña</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Nueva contraseña</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Mínimo 8 caracteres con variedad" />
              <PasswordStrengthBar password={password} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirmar contraseña</Label>
              <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </div>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Actualizando..." : "Actualizar contraseña"}
            </Button>
          </form>
          <div className="mt-4 text-sm">
            <Link className="text-primary hover:underline" to="/auth/login">
              Volver al login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
